import { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import app from 'new-ui/app';
import { useSafeState } from 'utils/hooks/useSafeState';
import { getUserWixId } from 'modules/user/utils';
import { useChat } from './useChat';
import { CHAT_ROLES, NBA_CHAT_ROOM } from './constants';
import { formmatedTrials } from './functions';
import { formmatedlDataForNba, sendMessageToNba } from './nbaFunctions';

const INIT_MESSAGE = {
  role: CHAT_ROLES.TINA,
  content: 'Hello! How can I help you today?',
};

const getPayload = ({ profile }) => {
  const userTrialList = formmatedTrials(app?.trials ?? []);
  const userSponsoredTrials = app.trials?.filter((trial) => trial?.isSponsored);
  const socTreatmentsCategories = (app.treatmentsCategories ?? [])
    .filter((e) => !e.treatment_category_key.includes('supportive_care'))
    .map((e) => e.treatment_category_key);

  return formmatedlDataForNba(
    profile,
    userTrialList,
    userSponsoredTrials ?? [],
    socTreatmentsCategories,
  );
};

export const useNbaChat = () => {
  const { data: profile } = useSelector((state) => state.profile);

  const [loading, setLoading] = useSafeState(false);

  const {
    chat: { initialUserMessage = null, userSystemPrompt = null, messageHistory: chatHistory } = {},
    updateChat,
    setMessages,
    addMessages,
    spliceMessages,
  } = useChat(NBA_CHAT_ROOM);

  const userWixId = getUserWixId(profile);

  useEffect(() => {
    if (chatHistory?.length) {
      return;
    }
    setMessages([INIT_MESSAGE]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const payload = getPayload({ profile });
    if (!initialUserMessage || Object.keys(payload).length === 0) {
      return;
    }

    const send = async () => {
      updateChat({ initialUserMessage: null, userSystemPrompt: null });

      setLoading(true);

      addMessages([{ role: CHAT_ROLES.ME, content: initialUserMessage }]);

      await sendMessageToNba({
        userWixId,
        userPrompt: initialUserMessage,
        payload,
        addMessages,
        userSystemPrompt,
      });

      setLoading(false);
    };

    send();
  }, [initialUserMessage, userSystemPrompt, chatHistory, updateChat, addMessages, userWixId, setLoading, profile]);

  const handleSendMessage = useCallback(async (messageToSend) => {
    const payload = getPayload({ profile });
    if (!messageToSend || !messageToSend.trim() || loading) {
      return;
    }
    if (!payload || !Object.keys(payload).length) {
      return;
    }

    setLoading(true);
    addMessages([{ content: messageToSend, role: CHAT_ROLES.ME }]);
    await sendMessageToNba({
      userWixId,
      userPrompt: messageToSend,
      payload,
      addMessages,
    });
    setLoading(false);
  }, [profile, loading, setLoading, addMessages, userWixId]);

  return {
    loading, handleSendMessage, chatHistory, spliceMessages, addMessages,
  };
};
